import { useCallback } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useSignalRHub, type ConnectionStatus } from '@/realtime/useSignalRHub';
import type { SigmaFilterInput, SigmaUpdateDto } from '@/api/types/sigma';

export interface SigmaSubscribeInput {
  filters: SigmaFilterInput;
  sigma: number;
  prevEquity: number;
  prevCumulativeProfit: number;
  lastStaticRefreshAt: string | null;
}

// Sigma live hub (/hubs/sigma). Subscribe(SigmaLiveSubscribeInput) on connect/resubscribe;
// server pushes SigmaUpdate {value, needsStaticRefresh, error}. Mirrors sigma-signalr.js.
export function useSigmaLive({
  enabled,
  getInput,
  onUpdate,
  onError,
}: {
  enabled: boolean;
  getInput: () => SigmaSubscribeInput;
  onUpdate: (payload: SigmaUpdateDto) => void;
  onError?: (message: string) => void;
}): { status: ConnectionStatus; resubscribe: () => void } {
  const { status, connection } = useSignalRHub({
    url: '/hubs/sigma',
    enabled,
    on: {
      SigmaUpdate: (payload) => onUpdate(payload as SigmaUpdateDto),
    },
    onConnected: (conn) => {
      void conn.invoke('Subscribe', getInput()).catch(() => onError?.('Failed to connect to live Sigma updates.'));
    },
    onDisconnecting: (conn) => {
      void conn.invoke('Unsubscribe', getInput()).catch(() => {});
    },
  });

  const resubscribe = useCallback(() => {
    const conn = connection.current;
    if (conn && conn.state === HubConnectionState.Connected) {
      void conn.invoke('Subscribe', getInput()).catch(() => onError?.('Failed to update live Sigma subscription.'));
    }
  }, [connection, getInput, onError]);

  return { status, resubscribe };
}
